import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { DashboardHeader } from "@/components/DashboardHeader";
import { ClinicalPageShell } from "@/components/clinical";
import { EventsQrTour } from "@/components/ProductTour";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Camera, CameraOff, Loader2, History } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export const Route = createFileRoute("/app/eventos/validar")({
  component: ValidadorQRPage,
  head: () => ({ meta: [{ title: "Validador QR — Structura Eventos" }] }),
});

type ScanResult = {
  ok: boolean;
  message: string;
  eventTitle: string | null;
  buyerName: string | null;
  qrCode: string;
  scannedAt: Date;
};

function ValidadorQRPage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastCodeRef = useRef<string | null>(null);
  const cooldownRef = useRef(false);

  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState<ScanResult | null>(null);
  const [history, setHistory] = useState<ScanResult[]>([]);
  const [manualCode, setManualCode] = useState("");
  const [sendingManual, setSendingManual] = useState(false);
  const [barcodeSupported] = useState(() => "BarcodeDetector" in window);

  const redeemCode = useCallback(async (qrCode: string) => {
    if (cooldownRef.current) return;
    cooldownRef.current = true;

    setScanning(true);
    try {
      const resp = await fetch("/api/eventos/redeem", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ qrCode }),
      });
      const data = await resp.json() as { ok: boolean; message: string; eventTitle?: string | null; buyerName?: string | null };
      const result: ScanResult = {
        ok: data.ok,
        message: data.message,
        eventTitle: data.eventTitle ?? null,
        buyerName: data.buyerName ?? null,
        qrCode,
        scannedAt: new Date(),
      };
      setLastResult(result);
      setHistory((h) => [result, ...h].slice(0, 10));
      if (data.ok) {
        toast.success(`Acceso válido — ${data.buyerName ?? ""}`, { duration: 3000 });
      } else {
        toast.error(data.message, { duration: 3000 });
      }
    } catch {
      const result: ScanResult = {
        ok: false,
        message: "Error de conexión",
        eventTitle: null,
        buyerName: null,
        qrCode,
        scannedAt: new Date(),
      };
      setLastResult(result);
      setHistory((h) => [result, ...h].slice(0, 10));
      toast.error("No se pudo conectar con el servidor");
    } finally {
      setScanning(false);
      // Cooldown de 2s para no reescanear el mismo código
      setTimeout(() => {
        cooldownRef.current = false;
        lastCodeRef.current = null;
      }, 2000);
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);

      if (barcodeSupported) {
        // Usar BarcodeDetector nativo (Chrome/Android)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const detector = new (window as any).BarcodeDetector({ formats: ["qr_code"] });
        scanIntervalRef.current = setInterval(async () => {
          if (!videoRef.current || scanning) return;
          try {
            const codes = await detector.detect(videoRef.current) as Array<{ rawValue: string }>;
            if (codes.length > 0) {
              const code = codes[0].rawValue;
              if (code && code !== lastCodeRef.current) {
                lastCodeRef.current = code;
                void redeemCode(code);
              }
            }
          } catch {
            // ignore detection errors
          }
        }, 500);
      } else {
        // Fallback: canvas + jsQR si está disponible
        scanIntervalRef.current = setInterval(async () => {
          if (!videoRef.current || !canvasRef.current || scanning) return;
          const video = videoRef.current;
          if (video.readyState !== video.HAVE_ENOUGH_DATA) return;
          const canvas = canvasRef.current;
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext("2d");
          if (!ctx) return;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          // Si jsQR está disponible en window
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const jsQR = (window as any).jsQR as ((data: Uint8ClampedArray, w: number, h: number) => { data: string } | null) | undefined;
          if (jsQR) {
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imageData.data, canvas.width, canvas.height);
            if (code?.data && code.data !== lastCodeRef.current) {
              lastCodeRef.current = code.data;
              void redeemCode(code.data);
            }
          }
        }, 300);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al acceder a la cámara";
      setCameraError(msg);
      toast.error("No se pudo acceder a la cámara: " + msg);
    }
  }, [barcodeSupported, redeemCode, scanning]);

  const stopCamera = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
    lastCodeRef.current = null;
  }, []);

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = manualCode.trim();
    if (!code) return;
    setSendingManual(true);
    await redeemCode(code);
    setManualCode("");
    setSendingManual(false);
  };

  return (
    <ClinicalPageShell>
      <EventsQrTour />
      <DashboardHeader title="Validador QR" subtitle="Escaneá entradas en la puerta del evento" />

      <div className="mx-auto max-w-md space-y-5">
        {/* Visor de cámara */}
        <div className="overflow-hidden rounded-2xl bg-black">
          <div className="relative aspect-square w-full">
            <video
              ref={videoRef}
              className="h-full w-full object-cover"
              playsInline
              muted
              style={{ display: cameraActive ? "block" : "none" }}
            />
            <canvas ref={canvasRef} className="hidden" />

            {!cameraActive && (
              <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-6 text-center text-white">
                <Camera className="h-16 w-16 opacity-40" />
                <p className="text-sm text-white/60">
                  {cameraError ? `Error: ${cameraError}` : "Cámara inactiva"}
                </p>
              </div>
            )}

            {/* Overlay de escaneo */}
            {cameraActive && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="relative h-56 w-56">
                  {/* Esquinas del visor */}
                  <div className="absolute left-0 top-0 h-8 w-8 border-l-4 border-t-4 border-white rounded-tl-lg" />
                  <div className="absolute right-0 top-0 h-8 w-8 border-r-4 border-t-4 border-white rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 h-8 w-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
                  <div className="absolute bottom-0 right-0 h-8 w-8 border-b-4 border-r-4 border-white rounded-br-lg" />
                  {scanning && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-white" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Botón cámara */}
        {cameraActive ? (
          <Button className="w-full" variant="destructive" onClick={stopCamera}>
            <CameraOff className="mr-2 h-5 w-5" /> Detener cámara
          </Button>
        ) : (
          <Button className="w-full h-14 text-base font-bold" onClick={() => void startCamera()}>
            <Camera className="mr-2 h-6 w-6" /> Escanear QR
          </Button>
        )}

        {/* Resultado último scan */}
        {lastResult && (
          <div
            className={`rounded-2xl p-5 text-center transition-all ${
              lastResult.ok
                ? "bg-emerald-500/15 border border-emerald-500/30"
                : "bg-red-500/15 border border-red-500/30"
            }`}
          >
            {lastResult.ok ? (
              <CheckCircle2 className="mx-auto mb-2 h-12 w-12 text-emerald-500" />
            ) : (
              <XCircle className="mx-auto mb-2 h-12 w-12 text-red-500" />
            )}
            <p className={`text-lg font-bold ${lastResult.ok ? "text-emerald-700" : "text-red-700"}`}>
              {lastResult.ok ? "Acceso válido" : "Acceso denegado"}
            </p>
            {lastResult.buyerName && (
              <p className="mt-1 text-sm font-medium text-foreground">{lastResult.buyerName}</p>
            )}
            {lastResult.eventTitle && (
              <p className="text-xs text-muted-foreground">{lastResult.eventTitle}</p>
            )}
            <p className={`mt-2 text-sm ${lastResult.ok ? "text-emerald-600" : "text-red-600"}`}>
              {lastResult.message}
            </p>
          </div>
        )}

        {/* Input manual fallback */}
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="mb-2 text-sm font-medium text-muted-foreground">Ingreso manual de código</p>
          <form onSubmit={(e) => void handleManualSubmit(e)} className="flex gap-2">
            <input
              className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="QR-tkt_xxx-..."
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
            />
            <Button type="submit" size="sm" disabled={sendingManual || !manualCode.trim()}>
              {sendingManual ? <Loader2 className="h-4 w-4 animate-spin" /> : "Validar"}
            </Button>
          </form>
        </div>

        {/* Historial de sesión */}
        {history.length > 0 && (
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <History className="h-4 w-4" /> Historial de sesión ({history.length})
            </div>
            <ul className="space-y-2">
              {history.map((r, i) => (
                <li key={i} className="flex items-center gap-3 text-xs">
                  {r.ok ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  ) : (
                    <XCircle className="h-4 w-4 shrink-0 text-red-500" />
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="font-medium text-foreground">{r.buyerName ?? r.qrCode.slice(0, 20)}</span>
                    {r.eventTitle && <span className="ml-1 text-muted-foreground">· {r.eventTitle}</span>}
                  </span>
                  <span className="shrink-0 text-muted-foreground">
                    {format(r.scannedAt, "HH:mm:ss", { locale: es })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </ClinicalPageShell>
  );
}
